import { useEffect } from "react";

import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import { sectionSelectors, selectCurrentSectionId, selectCurrentUserId } from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { useListSelector, usePromiseEffect } from "store/store-hooks";
import { trackUserProperty } from "utils/amplitude";
import { useHomeUrl } from "utils/hooks/use-home-url";
import { Outlet, useNavigate, useParams } from "utils/routing";
import { getDefaultSectionId } from "utils/section";
import { sectionHomeUrl, useViewLevel } from "utils/urls";

export default function Layout() {
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const sectionIds = useListSelector(sectionSelectors.selectIds);
  const userId = useAppSelector(selectCurrentUserId);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const viewLevel = useViewLevel();
  const homeUrl = useHomeUrl();

  // Ensure this component refreshes when we navigate between section routes
  const pathParams = useParams();
  const routeSectionId = pathParams.sectionId;

  const { isLoading, data } = usePromiseEffect(() => {
    if (userId) {
      return storeApi.sections.list({}, { skipToast: true });
    }
    return Promise.resolve(null);
  }, [userId]);

  useEffect(() => {
    if (viewLevel.sectionLink) {
      return;
    }

    if (!userId || isLoading || !data) {
      return;
    }

    if (sectionIds.length === 0) {
      navigate(homeUrl);
      return;
    }

    if (!routeSectionId || !sectionIds.includes(routeSectionId)) {
      const newRouteSectionId: string = currentSectionId || getDefaultSectionId(sectionIds);

      navigate(sectionHomeUrl(newRouteSectionId));
      return;
    }

    if (routeSectionId && currentSectionId !== routeSectionId) {
      dispatch(viewStateActions.setCurrentSectionId(routeSectionId));
      trackUserProperty("courseId", routeSectionId);
    }
  }, [
    routeSectionId,
    currentSectionId,
    viewLevel.sectionLink,
    dispatch,
    navigate,
    homeUrl,
    isLoading,
    data,
    sectionIds,
    userId,
  ]);

  return <Outlet />;
}
